import { useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import UndoIcon from "@mui/icons-material/Undo";
import { Box, Alert, Button, AlertTitle } from "@mui/material";

import { Avatar, DropdownMenu } from "subframe";
import * as SubframeCore from "@subframe/core";
import { SidebarLevo } from "subframe/components/SidebarLevo";

import useUser from "hooks/userHooks";

import { remove } from "utils/localStorage";
import { getAcronym, isEmpty as isStringEmpty } from "utils/string";

import { mixpanelLogoutEvent } from "services/mixpanel";
import SearchEmployeesDialog from "./SearchEmployeesDialog";

import Logo from "../../../assets/images/logoV2.svg";

export default function Retention() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchEmployeesDialogRef = useRef(null);

  const { user, isLoading: isLoadingUser } = useUser();

  const handleLogout = () => {
    remove("token");
    remove("refreshToken");
    remove("userRole");
    remove("email");
    mixpanelLogoutEvent(); // Emit logout analytics event

    navigate("/");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
        }}
      >
        <SidebarLevo logoImage={Logo}>
          <div className="grow shrink-0 basis-0 h-full w-full flex-col gap-3 items-start flex">
            <SidebarLevo.SidebarItem
              icon="FeatherSearch"
              selected={location.pathname === "/retention/V2/employees/search"}
              onClick={() => {
                searchEmployeesDialogRef.current?.setOpen(true);
              }}
            >
              Search
            </SidebarLevo.SidebarItem>
            <SidebarLevo.SidebarItem
              icon="FeatherInbox"
              selected={location.pathname === "/retention/V2/inbox"}
              onClick={() => navigate("/retention/V2/inbox")}
            >
              Inbox
            </SidebarLevo.SidebarItem>
            <SidebarLevo.SidebarItem
              icon="FeatherBarChart2"
              selected={location.pathname.startsWith("/retention/V2/reports")}
              onClick={() => navigate("/retention/V2/reports")}
            >
              Reports
            </SidebarLevo.SidebarItem>
            <SidebarLevo.SidebarItem
              icon="FeatherUsers"
              selected={location.pathname.startsWith("/retention/V2/roster")}
              onClick={() => navigate("/retention/V2/roster")}
            >
              Employees
            </SidebarLevo.SidebarItem>
            <SidebarLevo.SidebarItem
              icon="FeatherSettings"
              selected={location.pathname === "/retention/V2/settings"}
              onClick={() => navigate("/retention/V2/settings")}
            >
              Settings
            </SidebarLevo.SidebarItem>
          </div>
          <div className="w-full flex-col gap-2 pt-6 pr-6 pb-6 pl-6 items-center justify-end flex">
            {!isLoadingUser && (
              <SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Trigger asChild={true}>
                  <Avatar className="cursor-pointer">
                    {getAcronym(user?.name)}
                  </Avatar>
                </SubframeCore.DropdownMenu.Trigger>
                <SubframeCore.DropdownMenu.Portal>
                  <SubframeCore.DropdownMenu.Content
                    side="right"
                    align="end"
                    sideOffset={8}
                    asChild={true}
                    style={{ zIndex: 99 }}
                  >
                    <DropdownMenu>
                      <div className="grow shrink-0 basis-0 h-full w-full flex-col items-start flex">
                        <div className="grow shrink-0 basis-0 h-full w-full flex-col pt-2 pr-3 pb-2 pl-3 items-start flex">
                          <span className="text-default-font text-body-bold font-body-bold">
                            {isStringEmpty(user?.name) ? "-" : user.name}
                          </span>
                        </div>
                        <div className="w-full flex-col pt-0.5 pb-0.5 items-start flex">
                          <div className="flex-none h-px w-full flex-col gap-2 items-center flex bg-neutral-300" />
                        </div>
                        <div
                          className="w-full h-8 gap-1.5 pr-3 pl-3 items-center flex cursor-pointer hover:bg-neutral-100 data-[highlighted]:bg-neutral-100"
                          onClick={handleLogout}
                        >
                          <SubframeCore.Icon
                            className="text-neutral-700 text-body font-body"
                            name="FeatherLogOut"
                          />
                          <span className="text-neutral-700 text-body font-body">
                            Log Out
                          </span>
                        </div>
                      </div>
                    </DropdownMenu>
                  </SubframeCore.DropdownMenu.Content>
                </SubframeCore.DropdownMenu.Portal>
              </SubframeCore.DropdownMenu.Root>
            )}
          </div>
        </SidebarLevo>
        <div className="w-full h-full">
          <Alert
            severity="info"
            sx={{
              top: 0,
              zIndex: 99,
              width: "100%",
              height: "75px",
              color: "#2563eb",
              position: "sticky",
              fontFamily: "Nunito",
              alignItems: "center",
              backgroundColor: "#eff6ff",
              "& .MuiAlert-icon": {
                color: "#2563eb",
              },
            }}
            action={
              <Button
                size="small"
                color="inherit"
                startIcon={<UndoIcon />}
                sx={{ textTransform: "none" }}
                onClick={() => navigate("/retention")}
              >
                Switch back
              </Button>
            }
          >
            <AlertTitle
              sx={{
                fontFamily: "Nunito",
              }}
            >
              Check out our new look!
            </AlertTitle>
            Designed to simplify your workflow.
          </Alert>
          <div className="h-[calc(100vh-75px)] overflow-y-auto">
            <Outlet />
          </div>
        </div>
      </Box>
      <SearchEmployeesDialog ref={searchEmployeesDialogRef} />
    </>
  );
}
